import { memo } from 'react';
import styled from 'styled-components';

import * as PRESTATIONS from '@wecasa/common/src/constants/prestations';
import { getBasketPrices } from '@wecasa/common/src/ducks/customer/basket-selectors';
import { useConfig } from '@wecasa/common/src/providers/config-provider';
import type { ChildcareBasketPrices } from '@wecasa/common/src/types/commons';

import type { CommonChildcareBasketProps } from '@wecasa/customer/src/components/basket/types';

import Separator from '@wecasa/web-ui/src/components/separator/separator';

import { useAppSelector } from '../../../store';
import { StyledBasketContainer } from '../common/common';
import NoContentYet from '../common/no-content-yet';
import Address from '../common/sections/address/address';
import ProChoices from '../common/sections/pros/pro-choices';
import RecurrentPrestation from './recurring/sections/prestations';
import SpotAppointment from './spot/sections/appointment';
import SpotPrestations from './spot/sections/prestations/prestations';

const StyledAddress = styled(Address)`
  margin-top: 20px;
`;

export type ChildcareBasketProps = CommonChildcareBasketProps;

const ChildcareBasket = ({
  activities,
  address,
  addressDetails,
  appointment,
  childcareHoursPerWeek,
  children,
  childrenCount,
  duration,
  hasFreeParking,
  prestation,
  proChoices,
  promotion,
  setAddressDetails,
  toggleHasFreeParking,
  universe,
  user,
  youngestChildAge,
  shouldShowPromoCode,
}: ChildcareBasketProps) => {
  const config = useConfig();
  const prices = useAppSelector((state) => getBasketPrices(state, config)) as ChildcareBasketPrices;

  const isRecurring = prestation === PRESTATIONS.RECURRING_CHILDCARE;

  return !prestation ? (
    <NoContentYet user={user} universe={universe.reference} />
  ) : (
    <StyledBasketContainer>
      {isRecurring ? (
        <>
          <StyledAddress
            address={address}
            addressDetails={addressDetails}
            hasFreeParking={hasFreeParking}
            setAddressDetails={setAddressDetails}
            toggleHasFreeParking={toggleHasFreeParking}
            hasTopSeparator={false}
          />
          <Separator />
          <RecurrentPrestation
            childcareHoursPerWeek={childcareHoursPerWeek}
            childrenCount={childrenCount}
            prices={prices}
            youngestChildAge={youngestChildAge}
          />
          <ProChoices proChoices={proChoices} hasBottomSeparator={false} hasTopSeparator />
        </>
      ) : (
        <>
          <Address
            address={address}
            addressDetails={addressDetails}
            hasFreeParking={hasFreeParking}
            hasTopSeparator={false}
            setAddressDetails={setAddressDetails}
            toggleHasFreeParking={toggleHasFreeParking}
          />
          <SpotPrestations
            activities={activities}
            childrenCount={childrenCount}
            duration={duration}
            prestation={prestation}
            prices={prices}
            promotion={promotion}
            user={user}
            shouldShowPromoCode={shouldShowPromoCode}
          />
          <SpotAppointment appointment={appointment} duration={duration} />
          <ProChoices proChoices={proChoices} hasBottomSeparator={false} hasTopSeparator />
        </>
      )}
      {children}
    </StyledBasketContainer>
  );
};

export default memo(ChildcareBasket);
